import { ref, computed, reactive, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

/**
 * Composable para gestión inteligente de breadcrumbs
 * Genera breadcrumbs automáticamente basado en rutas, permisos y contexto
 */
export function useBreadcrumbs(options = {}) {
  const route = useRoute()
  const router = useRouter()
  
  // Configuración por defecto
  const defaultOptions = {
    enableCache: true,
    maxCacheSize: 50,
    enableAnimations: true,
    showIcons: true,
    homeRoute: { name: 'Welcome', path: '/welcome' },
    separatorIcon: 'ri-arrow-right-s-line',
    fallbackIcon: 'ri-folder-line',
    debug: false
  }
  
  const config = { ...defaultOptions, ...options }
  
  // Estado reactivo
  const breadcrumbs = ref([])
  const isLoading = ref(false)
  const cache = reactive(new Map())
  const customRouteNames = reactive({})
  
  // Mapeo inteligente de rutas a nombres legibles
  const smartRouteMapping = reactive({
    // Página de bienvenida
    welcome: { name: 'Bienvenid@', icon: 'ri-home-heart-line' },
    // Módulos principales
    'actualizacion-masiva': { name: 'Actualización Masiva', icon: 'ri-refresh-line', permission: 'bulk_update' },
    'actualizacion-masiva-v2': { name: 'Actualización Masiva V2', icon: 'ri-refresh-line', permission: 'bulk_update' },
    activity: { name: 'Actividad', icon: 'ri-pulse-line', permission: 'activity' },
    'master-4tool': { name: 'Master 4Tool', icon: 'ri-tools-fill', permission: 'master_4tool' },
    'borrado-registros': { name: 'Borrado de Registros', icon: 'ri-delete-bin-line', permission: 'delete_records' },
    users: { name: 'Usuarios', icon: 'ri-user-settings-line', permission: 'users' },
    tables: { name: 'Administración', icon: 'ri-admin-line', permission: 'panel_admin' },
    
    // Acciones comunes
    create: { name: 'Crear', icon: 'ri-add-line' },
    new: { name: 'Nuevo', icon: 'ri-add-circle-line' },
    edit: { name: 'Editar', icon: 'ri-edit-line' },
    view: { name: 'Ver', icon: 'ri-eye-line' },
    list: { name: 'Lista', icon: 'ri-list-check' },
    details: { name: 'Detalles', icon: 'ri-information-line' },
    settings: { name: 'Configuración', icon: 'ri-settings-4-line' },
    profile: { name: 'Perfil', icon: 'ri-user-line' },
    
    // Categorías específicas
    reports: { name: 'Reportes', icon: 'ri-file-chart-line' },
    analytics: { name: 'Análisis', icon: 'ri-bar-chart-line' },
    security: { name: 'Seguridad', icon: 'ri-shield-check-line' },
    database: { name: 'Base de Datos', icon: 'ri-database-line' },
    api: { name: 'API', icon: 'ri-code-line' },
    logs: { name: 'Registros', icon: 'ri-file-list-3-line' },
    monitoring: { name: 'Monitoreo', icon: 'ri-pulse-line' },
    backup: { name: 'Respaldo', icon: 'ri-save-line' },
    
    // Estados y acciones específicas
    import: { name: 'Importar', icon: 'ri-download-line' },
    export: { name: 'Exportar', icon: 'ri-upload-line' },
    search: { name: 'Búsqueda', icon: 'ri-search-line' },
    filter: { name: 'Filtros', icon: 'ri-filter-line' },
    history: { name: 'Historial', icon: 'ri-history-line' },
    trash: { name: 'Papelera', icon: 'ri-delete-bin-line' },
    archive: { name: 'Archivo', icon: 'ri-archive-line' }
  })
  
  // Cache de breadcrumbs para mejorar performance
  const getCacheKey = (routePath, userPermissions) => {
    const permissionsHash = userPermissions ? Object.keys(userPermissions).sort().join(',') : 'no-perms'
    return `${routePath}|${permissionsHash}`
  }
  
  // Limpiar cache cuando exceda el tamaño máximo
  const cleanCache = () => {
    if (cache.size > config.maxCacheSize) {
      const keys = Array.from(cache.keys())
      const toDelete = keys.slice(0, Math.floor(config.maxCacheSize / 2))
      toDelete.forEach(key => cache.delete(key))
    }
  }
  
  /**
   * Normaliza un segmento de ruta para búsqueda
   */
  const normalizeSegment = (segment) => {
    return segment
      .toLowerCase()
      .replace(/[-_]/g, '-')
      .replace(/([a-z])([A-Z])/g, '$1-$2')
      .toLowerCase()
  }
  
  /**
   * Verifica si el usuario tiene permisos para ver una ruta
   */
  const hasPermissionForRoute = (segment, userPermissions) => {
    if (!userPermissions) return true
    
    const routeConfig = smartRouteMapping[normalizeSegment(segment)]
    if (routeConfig && routeConfig.permission) {
      return userPermissions[routeConfig.permission] === true
    }
    
    return true // Si no hay restricción de permisos, permitir acceso
  }
  
  /**
   * Obtiene información inteligente de un segmento de ruta
   */
  const getRouteSegmentInfo = (segment, index, allSegments, userPermissions) => {
    const normalizedSegment = normalizeSegment(segment)
    
    // Verificar mapeo personalizado primero
    if (customRouteNames[normalizedSegment]) {
      return customRouteNames[normalizedSegment]
    }
    
    // Verificar mapeo inteligente
    if (smartRouteMapping[normalizedSegment]) {
      const routeInfo = smartRouteMapping[normalizedSegment]
      
      // Verificar permisos si está definido
      if (routeInfo.permission && !hasPermissionForRoute(segment, userPermissions)) {
        return null // No mostrar si no tiene permisos
      }
      
      return {
        name: routeInfo.name,
        icon: config.showIcons ? routeInfo.icon : null,
        permission: routeInfo.permission
      }
    }
    
    // Detectar IDs numéricos
    if (/^\d+$/.test(segment)) {
      return {
        name: `#${segment}`,
        icon: config.showIcons ? 'ri-hashtag' : null,
        isId: true
      }
    }
    
    // Detectar UUIDs
    if (/^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(segment)) {
      return {
        name: `ID: ${segment.substr(0, 8)}...`,
        icon: config.showIcons ? 'ri-key-2-line' : null,
        isUuid: true
      }
    }
    
    // Detectar fechas (YYYY-MM-DD)
    if (/^\d{4}-\d{2}-\d{2}$/.test(segment)) {
      const date = new Date(segment)
      return {
        name: date.toLocaleDateString('es-ES', { 
          day: 'numeric', 
          month: 'long', 
          year: 'numeric' 
        }),
        icon: config.showIcons ? 'ri-calendar-line' : null,
        isDate: true
      }
    }
    
    // Fallback: capitalizar primera letra y limpiar
    const cleanName = segment
      .replace(/[-_]/g, ' ')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ')
    
    return {
      name: cleanName,
      icon: config.showIcons ? config.fallbackIcon : null,
      isGenerated: true
    }
  }
  
  /**
   * Genera breadcrumbs inteligentes basados en la ruta actual
   */
  const generateBreadcrumbs = (routeInfo, userPermissions = null) => {
    const { path, name, meta } = routeInfo
    
    // Verificar cache primero
    if (config.enableCache) {
      const cacheKey = getCacheKey(path, userPermissions)
      if (cache.has(cacheKey)) {
        if (config.debug) {
          console.log('🔄 Breadcrumbs obtenidos del cache:', cacheKey)
        }
        return cache.get(cacheKey)
      }
    }
    
    const breadcrumbsArray = []
    
    // Breadcrumb de inicio (siempre presente)
    breadcrumbsArray.push({
      name: 'Inicio',
      path: config.homeRoute.path,
      route: config.homeRoute,
      icon: config.showIcons ? 'ri-home-line' : null,
      isHome: true
    })
    
    // Si estamos en la página de inicio, retornar solo eso
    if (path === config.homeRoute.path || name === config.homeRoute.name) {
      if (config.enableCache) {
        const cacheKey = getCacheKey(path, userPermissions)
        cache.set(cacheKey, breadcrumbsArray)
        cleanCache()
      }
      return breadcrumbsArray
    }
    
    // Dividir el path en segmentos
    const segments = path.split('/').filter(segment => segment.length > 0)
    let currentPath = ''
    
    // Procesar cada segmento
    segments.forEach((segment, index) => {
      currentPath += `/${segment}`
      
      // Obtener información del segmento
      const segmentInfo = getRouteSegmentInfo(segment, index, segments, userPermissions)
      
      // Si no tiene permisos, saltar este segmento
      if (!segmentInfo) {
        return
      }
      
      // Crear breadcrumb
      const breadcrumbItem = {
        name: segmentInfo.name,
        path: currentPath,
        route: { path: currentPath },
        icon: segmentInfo.icon,
        isId: segmentInfo.isId,
        isUuid: segmentInfo.isUuid,
        isDate: segmentInfo.isDate,
        isGenerated: segmentInfo.isGenerated,
        permission: segmentInfo.permission
      }
      
      // Si es el último segmento y tenemos meta información de la ruta
      if (index === segments.length - 1 && meta) {
        if (meta.breadcrumb) {
          breadcrumbItem.name = meta.breadcrumb
        }
        if (meta.icon && config.showIcons) {
          breadcrumbItem.icon = meta.icon
        }
      }
      
      breadcrumbsArray.push(breadcrumbItem)
    })
    
    // Eliminar duplicados consecutivos
    const cleanedBreadcrumbs = breadcrumbsArray.filter((item, index) => {
      if (index === 0) return true
      const prevItem = breadcrumbsArray[index - 1]
      return item.name !== prevItem.name || item.path !== prevItem.path
    })
    
    // Guardar en cache
    if (config.enableCache) {
      const cacheKey = getCacheKey(path, userPermissions)
      cache.set(cacheKey, cleanedBreadcrumbs)
      cleanCache()
    }
    
    if (config.debug) {
      console.log('🍞 Breadcrumbs generados:', cleanedBreadcrumbs)
    }
    
    return cleanedBreadcrumbs
  }
  
  /**
   * Registra un nombre personalizado para una ruta
   */
  const registerCustomRoute = (routeSegment, config) => {
    const normalizedSegment = normalizeSegment(routeSegment)
    customRouteNames[normalizedSegment] = config
  }
  
  /**
   * Registra múltiples rutas personalizadas
   */
  const registerCustomRoutes = (routesConfig) => {
    Object.entries(routesConfig).forEach(([segment, config]) => {
      registerCustomRoute(segment, config)
    })
  }
  
  /**
   * Actualiza el mapeo inteligente
   */
  const updateSmartMapping = (newMapping) => {
    Object.assign(smartRouteMapping, newMapping)
  }
  
  /**
   * Limpia el cache de breadcrumbs
   */
  const clearCache = () => {
    cache.clear()
  }
  
  /**
   * Navega a un breadcrumb específico
   */
  const navigateToBreadcrumb = (breadcrumb) => {
    if (breadcrumb.route) {
      router.push(breadcrumb.route)
    } else if (breadcrumb.path) {
      router.push(breadcrumb.path)
    }
  }
  
  // Computed para breadcrumbs reactivos
  const currentBreadcrumbs = computed(() => {
    return generateBreadcrumbs(route)
  })
  
  // Watcher para actualizar breadcrumbs cuando cambie la ruta
  watch(
    () => route.path,
    () => {
      isLoading.value = true
      // Pequeño delay para smooth transitions
      setTimeout(() => {
        breadcrumbs.value = generateBreadcrumbs(route)
        isLoading.value = false
      }, config.enableAnimations ? 100 : 0)
    },
    { immediate: true }
  )
  
  // API pública del composable
  return {
    // Estado reactivo
    breadcrumbs: currentBreadcrumbs,
    isLoading,
    
    // Métodos de configuración
    registerCustomRoute,
    registerCustomRoutes,
    updateSmartMapping,
    
    // Métodos de utilidad
    generateBreadcrumbs,
    navigateToBreadcrumb,
    clearCache,
    
    // Getters
    getCacheSize: () => cache.size,
    getSmartMapping: () => ({ ...smartRouteMapping }),
    getCustomRoutes: () => ({ ...customRouteNames }),
    
    // Configuración
    config
  }
}

/**
 * Configuraciones predefinidas para diferentes tipos de aplicaciones
 */
export const breadcrumbPresets = {
  // Configuración para aplicaciones administrativas
  admin: {
    homeRoute: { name: 'Welcome', path: '/welcome' },
    showIcons: true,
    enableAnimations: true,
    enableCache: true
  },
  
  // Configuración para aplicaciones simples
  simple: {
    homeRoute: { name: 'Home', path: '/' },
    showIcons: false,
    enableAnimations: false,
    enableCache: false
  },
  
  // Configuración para aplicaciones de e-commerce
  ecommerce: {
    homeRoute: { name: 'Store', path: '/store' },
    showIcons: true,
    enableAnimations: true,
    separatorIcon: 'ri-arrow-right-line'
  }
}

/**
 * Hook simplificado para breadcrumbs básicos
 */
export function useSimpleBreadcrumbs(preset = 'admin') {
  const presetConfig = breadcrumbPresets[preset] || breadcrumbPresets.admin
  return useBreadcrumbs(presetConfig)
}

export default useBreadcrumbs